
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Send, Sparkles, Calendar, Users, MapPin, Heart,
  Loader2, ChevronDown, ChevronUp, Wine
} from 'lucide-react';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { useAIMemory } from '@/hooks/useAIMemory';
import { useAIContext } from '@/hooks/useAIContext';
import { toast } from '@/lib/utils/toast';
import { withProfiler, addBreadcrumb, captureException } from '@/lib/sentry-wrapper';
import clsx from 'clsx';
import { supabase } from '@/lib/supabase/client';

interface RemyChatProps {
  isOpen: boolean;
  onClose: () => void;
  cartItems?: any[];
  savedPlaces?: any[];
  city?: string;
  onSaveVenue?: (venue: any) => void;
  onOptimizePlan?: (dates: string[], partySize: number) => void;
  onVectorSearchUpdate?: (venues: any[], context: string) => void;
  embedded?: boolean; // When true, removes modal wrapper and fits container
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  venues?: any[];
  isStreaming?: boolean;
  suggestions?: string[];
}

const RemyChatComponent = ({
  isOpen,
  onClose,
  cartItems = [],
  savedPlaces = [],
  city = '',
  onSaveVenue,
  onOptimizePlan: _onOptimizePlan,
  onVectorSearchUpdate,
  embedded = false
}: RemyChatProps) => {
  const { user } = useUnifiedAuth();
  const { memory, addMessage: addToMemory } = useAIMemory();
  const { context: aiContext } = useAIContext();
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [savedVenueIds, setSavedVenueIds] = useState<Set<string>>(new Set());
  const [partySize, setPartySize] = useState(2);
  const [showSavedPanel, setShowSavedPanel] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup abort controller on unmount to prevent background fetches
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const [allShownVenues, setAllShownVenues] = useState<Map<string, any>>(new Map());
  const [venuesByTurn, setVenuesByTurn] = useState<Array<{ turnId: string; venues: any[] }>>([]);
  const MAX_CONTEXT_TURNS = 3; // Keep venues from last 3 turns
  const MAX_CONTEXT_VENUES = 20; // Maximum venues to send as context
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [showTripSettings, setShowTripSettings] = useState(false);
  const [, setPresenceCount] = useState(0);
  const presenceChannelRef = useRef<any>(null);
  const [isRealtimeVoiceEnabled] = useState(false); // OpenAI Realtime feature flag (off for now)
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);
  const [_otherTyping, setOtherTyping] = useState(false);
  const otherTypingTimeoutRef = useRef<any>(null);

  useEffect(() => {
    return () => {
      if (otherTypingTimeoutRef.current) {
        clearTimeout(otherTypingTimeoutRef.current);
      }
    };
  }, []);
  const [webSearchAllowed] = useState(true);
  const [showSavedDropdown, setShowSavedDropdown] = useState(false);
  
  const chatEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  const getSmartVenueContext = useCallback(() => {
    const contextVenues: any[] = [];
    const seenIds = new Set<string>();

    // 1. Always include saved/bookmarked venues first (highest priority)
    savedPlaces?.forEach(venue => {
      if (!seenIds.has(venue.id)) {
        contextVenues.push({
          id: venue.id,
          name: venue.place_name || venue.name,
          category: venue.category,
          vibe: venue.vibe,
          neighborhood: venue.detected_neighborhood || venue.neighborhood,
          cuisine: venue.cuisine_type,
          price: venue.price_level,
          isSaved: true
        });
        seenIds.add(venue.id);
      }
    });

    // 2. Add venues from recent turns (last 3 conversations)
    const recentTurns = venuesByTurn.slice(-MAX_CONTEXT_TURNS);
    recentTurns.reverse().forEach(turn => { // Most recent first
      turn.venues.forEach(venue => {
        if (!seenIds.has(venue.id) && contextVenues.length < MAX_CONTEXT_VENUES) {
          contextVenues.push({
            id: venue.id,
            name: venue.place_name || venue.name,
            category: venue.category,
            vibe: venue.vibe,
            neighborhood: venue.detected_neighborhood || venue.neighborhood,
            cuisine: venue.cuisine_type,
            price: venue.price_level
          });
          seenIds.add(venue.id);
        }
      });
    });

    // 3. If user mentions a venue by name, ensure it's included
    const mentionedInLastMessage = input.toLowerCase();
    allShownVenues.forEach((venue, id) => {
      const venueName = (venue.place_name || venue.name || '').toLowerCase();
      if (venueName && mentionedInLastMessage.includes(venueName) && !seenIds.has(id)) {
        // Add to front of array (high priority)
        contextVenues.unshift({
          id: venue.id,
          name: venue.place_name || venue.name,
          category: venue.category,
          vibe: venue.vibe,
          neighborhood: venue.detected_neighborhood || venue.neighborhood,
          cuisine: venue.cuisine_type,
          price: venue.price_level,
          isMentioned: true
        });
        seenIds.add(id);
      }
    });

    return contextVenues.slice(0, MAX_CONTEXT_VENUES);
  }, [savedPlaces, venuesByTurn, allShownVenues, input]);

  const features = {
    supabaseRealtime: true,
    experimentalSearch: true,
    openAIRealtimeVoice: false
  } as const;
  
  const formatAssistantText = useCallback((text: string, venueNumbers?: Map<string, number>): React.ReactNode => {
    if (!text) return '';
    
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    
    return parts.map((part, index) => {
      const venueMatch = part.match(/\*\*([^*]+)\*\*/);
      if (venueMatch) {
        const venueName = venueMatch[1];
        const venueNumber = venueNumbers?.get(venueName.toLowerCase());
        return (
          <span 
            key={index}
            className="inline-block px-1.5 py-0.5 mx-0.5 bg-olive/20 text-bone rounded font-medium relative"
            data-venue={venueName}
          >
            {venueNumber && (
              <span className="inline-block w-4 h-4 bg-olive text-bone text-xs rounded-full mr-1 text-center leading-4">
                {venueNumber}
              </span>
            )}
            {venueName}
          </span>
        );
      }
      // Return regular text preserving line breaks
      return <span key={index}>{part}</span>;
    });
  }, []);

  // Basic city detector for free text (kept client-side)
  const detectCityFromText = useCallback((t: string): string | undefined => {
    if (!t) return undefined;
    const s = t.toLowerCase();
    if (/(nyc|new york|brooklyn|manhattan|queens|bronx|williamsburg|soho)/i.test(s)) return 'New York';
    if (/(los angeles|\bla\b|west hollywood|weho|santa monica|venice)/i.test(s)) return 'Los Angeles';
    if (/(miami|wynwood|south beach|brickell)/i.test(s)) return 'Miami';
    if (/(berlin|kreuzberg|neukolln|mitte|prenzlauer)/i.test(s)) return 'Berlin';
    if (/(london|shoreditch|soho|hackney)/i.test(s)) return 'London';
    if (/(paris|le marais|saint-germain)/i.test(s)) return 'Paris';
    if (/(lisbon|lisboa|bairro alto|alfama|principe real)/i.test(s)) return 'Lisbon';
    return undefined;
  }, []);

  // Keep the old function for backward compatibility but update it
  const sanitizeAssistantText = useCallback((t: string): string => {
    if (!t) return '';
    return t.replace(/\*\*([^*]+)\*\*/g, '$1').trim();
  }, []);

  const _extractVenueNamesFromText = useCallback((t: string): string[] => {
    if (!t) return [];
    const names = new Set<string>();
    const boldRe = /\*\*([^*]+)\*\*/g;
    let m: RegExpExecArray | null;
    while ((m = boldRe.exec(t)) !== null) {
      const name = m[1].trim();
      if (name) names.add(name);
      if (names.size >= 5) break;
    }
    // Fallback: numbered list lines like "1) Name —"
    if (names.size === 0) {
      t.split(/\n+/).forEach(line => {
        const mm = line.match(/^\s*\d+\)\s+([^—:\-]+)[—:\-]?/);
        if (mm && mm[1]) names.add(mm[1].trim());
      });
    }
    return Array.from(names);
  }, []);

  // Stable ID for venues across sources
  const getStableId = useCallback((v: any): string => {
    if (!v) return '';
    return (
      v.id ||
      v.google_place_id ||
      `${(v.place_name || v.name || 'venue')}-${v.neighborhood || v.detected_neighborhood || city}`
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
    );
  }, [city]);
  
  const handleSaveAllVenues = useCallback((venues: any[]) => {
    if (!onSaveVenue) return;
    
    const seen = new Set<string>();
    const unique = venues.filter(v => {
      const sid = getStableId(v);
      if (!sid || seen.has(sid)) return false;
      seen.add(sid);
      return true;
    });
    const unsavedVenues = unique.filter(v => !savedVenueIds.has(getStableId(v)));
    unsavedVenues.forEach(venue => {
      onSaveVenue(venue);
      setSavedVenueIds(prev => new Set(prev).add(getStableId(venue)));
    });
    
    if (unsavedVenues.length > 0) {
      toast.success(`Added ${unsavedVenues.length} venues to your plan!`);
    }
  }, [onSaveVenue, savedVenueIds, getStableId]);
  
  const discoveryVariants = [
    (city: string) => `Surprise me with hidden patios and cozy corners in ${city}.`,
    (city: string) => `Natural wine + great snacks in ${city} — 5 spots.`,
    (city: string) => `Late-night bar snacks and cocktails in ${city}.`,
    (city: string) => `Neighborhood gems where locals go in ${city}.`,
    (city: string) => `Classic date-night counters in ${city}, low lighting.`,
    (city: string) => `Warm terraces and good energy in ${city}.`,
    (city: string) => `New openings worth a look in ${city}.`,
    (city: string) => `Great Italian in ${city} beyond the obvious.`,
    (city: string) => `Third places for long conversations in ${city}.`,
    (city: string) => `Chef-driven spots with a scene in ${city}.`
  ];

  const getSmartPills = useCallback(() => {
    const pills: any[] = [];

    const lastAssistant = messages.filter(m => m.role === 'assistant').pop();
    if (lastAssistant?.venues && lastAssistant.venues.length > 0) {
      // Deduplicate and cap to top 5 (avoid overwhelming)
      const venues = lastAssistant.venues as any[];
      const dedup = [] as any[];
      const seen = new Set<string>();
      for (const v of venues) {
        const sid = getStableId(v);
        if (sid && !seen.has(sid)) {
          seen.add(sid);
          dedup.push(v);
        }
        if (dedup.length >= 5) break;
      }
      const label = dedup.length > 3 ? `Save top ${Math.min(3, dedup.length)}` : `Save all`;
      pills.push({ 
        text: label, 
        icon: Heart, 
        action: () => handleSaveAllVenues(dedup.slice(0, 3)) 
      });
      pills.push({ text: 'More like these?', icon: Sparkles, prompt: 'Find more like these' });
    }

    const hasCity = city && city.trim() !== '';
    
    if (!hasCity) {
      pills.push({ text: 'NYC', icon: MapPin, prompt: 'Show me the best spots in New York' });
      pills.push({ text: 'LA', icon: MapPin, prompt: 'What are the top picks in Los Angeles?' });
      pills.push({ text: 'London', icon: MapPin, prompt: 'Curate recommendations for London' });
    } else {
      const savedName = (savedPlaces?.[savedPlaces.length - 1]?.place_name) || (savedPlaces?.[savedPlaces.length - 1]?.name) || null;
      const variant = discoveryVariants[Math.floor(Math.random() * discoveryVariants.length)];
      const baseDiscover = variant(city);
      const discoverPrompt = savedName
        ? `Because I saved ${savedName}, ${baseDiscover}`
        : baseDiscover;
      pills.push({ text: 'Discover', icon: Sparkles, prompt: discoverPrompt });

      const hour = new Date().getHours();
      let timeLabel = 'Now';
      if (hour >= 5 && hour < 12) timeLabel = 'Morning';
      else if (hour >= 12 && hour < 17) timeLabel = 'Afternoon';
      else if (hour >= 17 && hour < 23) timeLabel = 'Tonight';
      else timeLabel = 'Late night';
      const timePrompt = `Curate great ${timeLabel.toLowerCase()} spots in ${city}?`;
      pills.push({ text: timeLabel, icon: Wine, prompt: timePrompt });

      pills.push({ text: 'Near me', icon: MapPin, prompt: `Great spots near me in ${city}?` });
    }

    return pills.slice(0, 3);
  }, [messages, savedPlaces, city, handleSaveAllVenues, getStableId]);


  // Supabase Realtime presence (lightweight)
  useEffect(() => {
    if (!isOpen || !features.supabaseRealtime) return;
    const sessionId = (memory as any)?.sessionId;
    if (!sessionId) return;

    const channel = supabase.channel(`ai_session:${sessionId}`, {
      config: { presence: { key: user?.id || 'anon' } }
    });
    presenceChannelRef.current = channel;

    channel.on('presence', { event: 'sync' }, () => {
      try {
        const state: Record<string, any[]> = channel.presenceState();
        let count = 0;
        for (const k in state) count += state[k].length;
        setPresenceCount(count);
      } catch {
        setPresenceCount(1);
      }
    });

    channel.on('broadcast', { event: 'typing' }, (_payload) => {
      try {
        if (otherTypingTimeoutRef.current) clearTimeout(otherTypingTimeoutRef.current);
        setOtherTyping(true);
        otherTypingTimeoutRef.current = setTimeout(() => setOtherTyping(false), 1500);
      } catch {}
    });

    channel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await channel.track({ role: 'viewer', ts: Date.now() });
      }
    });

    return () => {
      try {
        channel.unsubscribe();
      } finally {
        presenceChannelRef.current = null;
        setPresenceCount(0);
      }
    };
  }, [isOpen, features.supabaseRealtime, (memory as any)?.sessionId, user?.id]);

  // Broadcast typing events (throttled)
  useEffect(() => {
    if (!presenceChannelRef.current || !features.supabaseRealtime) return;
    if (!input) return;
    const t = setTimeout(() => {
      try {
        presenceChannelRef.current.send({
          type: 'broadcast',
          event: 'typing',
          payload: { userId: user?.id || 'anon', ts: Date.now() }
        });
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [input, features.supabaseRealtime, user?.id]);

  useEffect(() => {
    if (!presenceChannelRef.current || !features.supabaseRealtime) return;
    try {
      presenceChannelRef.current.send({
        type: 'broadcast',
        event: 'saved_count_update',
        payload: { count: savedPlaces.length, ts: Date.now() }
      });
    } catch {}
  }, [savedPlaces.length, features.supabaseRealtime]);

  useEffect(() => {
    if (!presenceChannelRef.current || !features.supabaseRealtime) return;
    try {
      presenceChannelRef.current.send({
        type: 'broadcast',
        event: 'context_update',
        payload: { partySize, dates: selectedDates, ts: Date.now() }
      });
    } catch {}
  }, [partySize, selectedDates.join(','), features.supabaseRealtime]);

  // OpenAI Realtime (placeholder)
  const connectRealtime = useCallback(async () => {
    if (!features.openAIRealtimeVoice) return;
    try {
      // Placeholder for OpenAI Realtime connection (WebRTC/DataChannel)
      // We'll wire this to the Voice model once enabled.
      setIsRealtimeConnected(true);
      toast.info('Voice (Realtime API) coming soon');
    } catch {
      setIsRealtimeConnected(false);
      toast.error('Failed to start voice');
    }
  }, [features.openAIRealtimeVoice]);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || isLoading) return;
    
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: Date.now()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setStreamingText('');
    
    abortControllerRef.current = new AbortController();
    
    try {
      let effectiveCity: string | undefined = city?.trim();
      if (!effectiveCity) {
        effectiveCity = detectCityFromText(text);
      }
      if (!effectiveCity) {
        const askCity: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: 'Quick one — which city are you exploring right now? (e.g., New York, Los Angeles, London)',
          timestamp: Date.now()
        };
        setMessages(prev => [...prev, askCity]);
        setIsLoading(false);
        setStreamingText('');
        return;
      }
      const resolvedCity = effectiveCity as string;
      const chatContext = {
        city: resolvedCity,
        cartItems: cartItems.map(item => ({
          id: item.id,
          name: item.place_name || item.name,
          type: item.place_type,
          category: item.category
        })),
        savedPlaces: savedPlaces.map(item => ({
          id: item.id,
          name: item.place_name || item.name
        })),
        partySize,
        userPreferences: (aiContext as any)?.preferences
      };
      
      const priorMessages = messages.slice(-8).map(m => ({ role: m.role, content: m.content }));

      const STREAMING_ENABLED = (import.meta as any).env?.VITE_REMY_STREAMING === 'true';

      if (STREAMING_ENABLED) {
      const visibleVenues = getSmartVenueContext();

      const requestBody = {
            prompt: text,
            city: resolvedCity,
            memory: {
              recentMessages: [...priorMessages, { role: 'user', content: text }],
              savedVenues: Array.from(savedVenueIds),
              messageCount: priorMessages.length + 1
            },
            // Include visible venues for stateless context injection
            visibleVenues,
            streaming: true,
            stream: true,
            sessionKey: (memory as any)?.sessionId,
            userId: user?.id,
            context: { ...chatContext, selectedDates },
            debug: false
          };
        
        const response = await fetch('/.netlify/functions/remy-ai', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
          body: JSON.stringify(requestBody),
          signal: abortControllerRef.current.signal
        });
        if (!response.ok) throw new Error('Failed to get response');

        const contentType = response.headers.get('content-type') || '';
        let fullMessage = '';
        let venuesReceived: any[] = [];

        if (contentType.includes('application/json')) {
          const data = await response.json();
          if (data?.rate_limited) {
            fullMessage = typeof data.error === 'string' && data.error.length > 0
              ? data.error
              : `Rate limit reached. Retry in ${data.retry_after_seconds || 15}s.`;
          }
          // Handle both 'content' (from SSEManager) and 'text' (from non-streaming)
          const messageContent = data.content || data.text || '';
          if (!fullMessage) {
            fullMessage = typeof messageContent === 'string'
              ? sanitizeAssistantText(messageContent)
              : sanitizeAssistantText(JSON.stringify(messageContent));
          }
          venuesReceived = data.venues || data.featuredSpots || [];
          if (data.clarifying) {
            const clarMsg: Message = {
              id: (Date.now() + 2).toString(),
              role: 'assistant',
              content: sanitizeAssistantText(String(data.clarifying)),
              timestamp: Date.now()
            };
            setMessages(prev => [...prev, clarMsg]);
          }
        } else {
          const reader = response.body?.getReader();
          const decoder = new TextDecoder();
          let rateLimitedMessage = '';
          if (reader) {
            for (;;) {
              const { done, value } = await reader.read();
              if (done) break;
              const chunk = decoder.decode(value);
              const lines = chunk.split('\n');
              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  const data = line.slice(6);
                  if (data === '[DONE]') continue;
                  try {
                    const parsed = JSON.parse(data);
                    if (parsed.rate_limited) {
                      rateLimitedMessage = typeof parsed.error === 'string' && parsed.error.length > 0
                        ? parsed.error
                        : `Rate limit reached. Retry in ${parsed.retry_after_seconds || 15}s.`;
                      continue;
                    }
                    if (parsed.content) {
                      fullMessage += parsed.content;
                      setStreamingText(fullMessage);
                    }
                    if (parsed.clarifying) {
                      const clarMsg: Message = {
                        id: (Date.now() + Math.random()).toString(),
                        role: 'assistant',
                        content: sanitizeAssistantText(String(parsed.clarifying)),
                        timestamp: Date.now()
                      };
                      setMessages(prev => [...prev, clarMsg]);
                    }
                    if (parsed.venues) venuesReceived = parsed.venues;
                  } catch {
                    continue;
                  }
                }
              }
            }

            if (!fullMessage && rateLimitedMessage) {
              fullMessage = rateLimitedMessage;
            }
          }
        }

        // TODO: Complete tracking implementation

        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: fullMessage,
          timestamp: Date.now(),
          venues: venuesReceived.length > 0 ? venuesReceived : undefined
        };
        setMessages(prev => [...prev, assistantMessage]);
        setStreamingText('');
        if (memory) addToMemory('assistant', fullMessage);

        // Accumulate venues for multi-turn context
        if (venuesReceived.length > 0) {
          const newAllShownVenues = new Map(allShownVenues);
          venuesReceived.forEach(venue => {
            newAllShownVenues.set(venue.id, venue);
          });
          setAllShownVenues(newAllShownVenues);

          const turnId = `turn_${Date.now()}`;
          setVenuesByTurn(prev => [...prev, { turnId, venues: venuesReceived }]);

          // TODO: Complete analytics tracking
        }

        if (onVectorSearchUpdate && venuesReceived.length > 0) {
          onVectorSearchUpdate(venuesReceived, text);
        }
        
        addBreadcrumb({
          category: 'remy-chat',
          message: 'Chat message (SSE) complete',
          level: 'info',
          data: { promptLength: text.length, hasVenues: (assistantMessage.venues?.length || 0) > 0 }
        });
      } else {
        const response = await fetch('/.netlify/functions/remy-ai', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            prompt: text,
            memory: {
              recentMessages: [...priorMessages, { role: 'user', content: text }],
              savedVenues: Array.from(savedVenueIds),
              messageCount: priorMessages.length + 1
            },
            city: resolvedCity,
            stage: 'discover',
            userContext: 'chat',
            streaming: false,
            context: { ...chatContext, selectedDates },
            aiSessionKey: (memory as any)?.sessionId,
            userId: user?.id,
            webSearchAllowed
          }),
          signal: abortControllerRef.current.signal
        });
        if (!response.ok) throw new Error('Failed to get response');
        const data = await response.json();
        const fullMessage = sanitizeAssistantText(data.text || data.content || '');
        const venuesReceived: any[] = data.featuredSpots || data.venues || [];

        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: fullMessage,
          timestamp: Date.now(),
          venues: venuesReceived.length > 0 ? venuesReceived : undefined
        };
        setMessages(prev => [...prev, assistantMessage]);
        setStreamingText('');
        if (memory) addToMemory('assistant', fullMessage);

        // Accumulate venues for multi-turn context (non-streaming path)
        if (venuesReceived.length > 0) {
          const newAllShownVenues = new Map(allShownVenues);
          venuesReceived.forEach(venue => {
            newAllShownVenues.set(venue.id, venue);
          });
          setAllShownVenues(newAllShownVenues);

          const turnId = `turn_${Date.now()}`;
          setVenuesByTurn(prev => [...prev, { turnId, venues: venuesReceived }]);
        }

        if (onVectorSearchUpdate && venuesReceived.length > 0) {
          onVectorSearchUpdate(venuesReceived, text);
        }
        
        addBreadcrumb({
          category: 'remy-chat',
          message: 'Chat message (JSON) complete',
          level: 'info',
          data: { promptLength: text.length, hasVenues: venuesReceived.length > 0 }
        });
      }
      
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: 'I had a brief hiccup! Could you try that again?',
          timestamp: Date.now()
        };
        setMessages(prev => [...prev, errorMessage]);
        
        captureException(error, {
          tags: { component: 'RemyChat', action: 'sendMessage' }
        });
      }
    } finally {
      setIsLoading(false);
      setStreamingText('');
      abortControllerRef.current = null;
    }
  }, [
    messages,
    memory,
    city,
    cartItems,
    savedPlaces,
    partySize,
    aiContext,
    selectedDates,
    savedVenueIds,
    addToMemory,
    user,
    allShownVenues,
    venuesByTurn,
    getSmartVenueContext,
    webSearchAllowed,
    onVectorSearchUpdate,
    addBreadcrumb,
    captureException
  ]);

  // Handle form submission - prevent viewport jumping
  const handleSendMessage = useCallback((e: React.FormEvent) => {
    e.preventDefault();

    if (!input.trim() || isLoading) return;

    const scrollContainer = messagesContainerRef.current;
    const scrollPos = scrollContainer?.scrollTop;

    sendMessage(input);
    setInput('');

    // On mobile, blur input to close keyboard after send
    if (window.innerWidth < 768) {
      const inputEl = e.currentTarget.querySelector('input');
      inputEl?.blur();

      // Restore scroll position after keyboard closes
      setTimeout(() => {
        if (scrollContainer && scrollPos !== undefined) {
          scrollContainer.scrollTop = scrollPos;
        }
      }, 100);
    }
  }, [input, isLoading, sendMessage]);

  const handlePillClick = (pill: any) => {
    if (pill.action) {
      pill.action();
    } else if (pill.prompt) {
      // Ensure pill-triggered prompts persist context/saves
      sendMessage(pill.prompt);
      try {
        if (pill.text === 'Near me') {
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                try {
                  localStorage.setItem('rv_geo_lat', String(pos.coords.latitude));
                  localStorage.setItem('rv_geo_lng', String(pos.coords.longitude));
                } catch {}
              },
              () => {}
            );
          }
        }
      } catch {}
    }
  };

  const _cancelStream = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsLoading(false);
      setStreamingText('');
    }
  };

  // Auto-scroll to bottom - without affecting viewport
  useEffect(() => {
    // Use container-based scrolling instead of scrollIntoView
    const scrollContainer = messagesContainerRef.current;
    if (scrollContainer) {
      const isNearBottom = scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight < 100;
      
      if (isNearBottom || messages.length === 1) {
        requestAnimationFrame(() => {
          scrollContainer.scrollTo({
            top: scrollContainer.scrollHeight,
            behavior: 'smooth'
          });
        });
      }
    }
  }, [messages, streamingText]);

  // Auto-open saved panel on having saves (first time per open)
  const openedSavedOnceRef = useRef(false);
  useEffect(() => {
    if (!openedSavedOnceRef.current && savedPlaces.length > 0) {
      setShowSavedPanel(true);
      openedSavedOnceRef.current = true;
    }
  }, [savedPlaces.length]);

  useEffect(() => {
    if (savedPlaces.length > 0) {
      setSavedVenueIds(new Set(savedPlaces.map(p => p.id)));
    }
  }, [savedPlaces]);

  if (!isOpen) return null;

  const smartPills = getSmartPills();

  // Chat content component (shared between embedded and modal)
  const chatContent = (
    <div className="h-full flex flex-col bg-charcoal">
      {/* Saved Recommendations Dropdown - Only in embedded mode */}
      {embedded && savedPlaces && savedPlaces.length > 0 && (
        <div className="flex-shrink-0 p-3 border-b border-ash/20">
          <button
            onClick={() => setShowSavedDropdown(!showSavedDropdown)}
            className="w-full flex items-center justify-between px-3 py-2 bg-olive/10 hover:bg-olive/20 
                     rounded-lg text-bone/90 text-sm transition-all"
          >
            <div className="flex items-center gap-2">
              <Heart className="w-4 h-4 text-olive" />
              <span>{savedPlaces.length} saved recommendations</span>
            </div>
            {showSavedDropdown ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          {showSavedDropdown && (
            <div className="mt-2 bg-charcoal-lighter rounded-lg border border-ash/20 max-h-60 overflow-y-auto">
              {savedPlaces.map((venue: any) => (
                <button
                  key={venue.id}
                  onClick={() => {
                    setInput(`Tell me more about ${venue.place_name}`);
                    setShowSavedDropdown(false);
                  }}
                  className="w-full text-left p-3 hover:bg-ash/10 transition-all border-b border-ash/10 last:border-0"
                >
                  <div className="font-medium text-bone text-sm">{venue.place_name}</div>
                  <div className="text-xs text-ash mt-0.5">
                    {venue.category} • {venue.neighborhood || venue.detected_neighborhood}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Messages Area with Infinite Scroll - prevent viewport interference */}
      <div 
        ref={messagesContainerRef} 
        className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-ash/20 scrollbar-track-transparent overscroll-contain"
        style={{
          // Prevent scroll chaining to viewport
          overscrollBehavior: 'contain',
          // Optimize scrolling performance
          WebkitOverflowScrolling: 'touch',
          // Ensure messages stay in their container
          position: 'relative'
        }}
      >
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <div className="mb-4 mt-8 md:mt-4">
              <Sparkles className="w-10 h-10 md:w-12 md:h-12 text-olive mx-auto mb-3" />
              <p className="text-bone text-base md:text-lg mb-2">Hi! I'm Remy, your AI concierge for {city}.</p>
              <p className="text-ash text-xs md:text-sm">Ask for ideas, save what you like, and I'll refine the short list.</p>
            </div>
            
            {/* Smart Pills */}
            <div className="flex flex-wrap gap-2 justify-center mt-4">
              {smartPills.map((pill, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setInput(pill.prompt);
                    // Automatically send the message
                    setTimeout(() => {
                      const form = document.querySelector('form') as HTMLFormElement;
                      if (form) {
                        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                      }
                    }, 100);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-charcoal-lighter/80 text-bone/90 rounded-full 
                           hover:bg-ash/40 hover:text-bone transition-all text-xs font-medium
                           border border-ash/30 hover:border-ash/50
                           shadow-sm hover:shadow"
                >
                  {pill.icon === MapPin && <span className="text-[11px]">📍</span>}
                  <span>{pill.text || pill.label}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((message, _index) => {
            const venueNumberMap = new Map<string, number>();
            if (message.venues && message.venues.length > 0) {
              for (const v of message.venues as any[]) {
                const venueName = (v.place_name || v.name || '').toLowerCase();
                venueNumberMap.set(venueName, (venueNumberMap.size || 0) + 1);
              }
            }
            
            return (
              <div key={message.id} className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {message.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-olive to-rust 
                                flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-bone" />
                  </div>
                )}
                <div className={`max-w-[85%] ${message.role === 'user' 
                  ? 'bg-olive text-bone px-3 py-2 rounded-xl text-sm' 
                  : 'bg-charcoal-lighter text-bone px-3 py-2 rounded-xl border border-ash/20 text-sm'}`}
                >
                  <div className="text-sm leading-relaxed text-bone">
                    {message.isStreaming || message.content === streamingText ? (
                      <div className="whitespace-pre-wrap">
                        {formatAssistantText(streamingText || message.content, venueNumberMap)}
                      </div>
                    ) : (
                      <div className="whitespace-pre-wrap">
                        {formatAssistantText(message.content, venueNumberMap)}
                      </div>
                    )}
                  </div>

                  {/* Inline Action List for assistant messages with venues */}
                  {message.role === 'assistant' && Array.isArray(message.venues) && message.venues.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button
                        className="px-2.5 py-1 text-xs rounded-full bg-olive/20 text-bone border border-olive/40"
                        onClick={() => {
                          const ids = (message.venues || []).map((v: any) => v.id).filter(Boolean);
                          if (ids.length === 0) return;
                          setInput('Save these');
                          // send a light command the backend understands
                          sendMessage('Save these');
                        }}
                      >Save these</button>
                      <button
                        className="px-2.5 py-1 text-xs rounded-full bg-charcoal border border-ash/30 text-bone/90"
                        onClick={() => sendMessage('Not quite right')}
                      >Not quite right</button>
                      <button
                        className="px-2.5 py-1 text-xs rounded-full bg-charcoal border border-ash/30 text-bone/90"
                        onClick={() => sendMessage('More like these')}
                      >More like these</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input Area - Pinned at Bottom */}
      <div className="flex-shrink-0 border-t border-ash/20 bg-charcoal">
        {/* Smart Pills Above Input */}
        {smartPills.length > 0 && (
          <div className="p-3 border-b border-ash/20">
            <div className="flex flex-wrap gap-2">
              {smartPills.map((pill, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setInput(pill.prompt);
                    // Automatically send the message
                    setTimeout(() => {
                      const form = document.querySelector('form') as HTMLFormElement;
                      if (form) {
                        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                      }
                    }, 100);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-charcoal-lighter/80 text-bone/90 rounded-full 
                           hover:bg-ash/40 hover:text-bone transition-all text-xs font-medium
                           border border-ash/30 hover:border-ash/50
                           shadow-sm hover:shadow"
                >
                  {pill.icon === MapPin && <span className="text-[11px]">📍</span>}
                  <span>{pill.text || pill.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Message Input */}
        <div className="p-3">
          <form onSubmit={handleSendMessage} className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask me anything about your trip..."
            className="flex-1 px-3 py-2 bg-charcoal-lighter border border-ash/20 
                     text-bone placeholder-ash rounded-lg focus:outline-none 
                     focus:ring-2 focus:ring-olive focus:border-transparent"
            disabled={isLoading}
            // Prevent zoom on mobile Safari (16px minimum)
            style={{ fontSize: '16px' }}
            onFocus={(e) => {
              if (window.innerWidth < 768) {
                // Prevent viewport manipulation
                e.preventDefault();
                setTimeout(() => {
                  const chatWindow = e.target.closest('.fixed');
                  if (chatWindow) {
                    // Ensure input stays visible without scrolling viewport
                    chatWindow.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
                  }
                }, 100);
              }
            }}
          />
          <button
            type="submit"
            disabled={!input.trim() || isLoading}
            className="px-3 py-2 bg-olive text-bone rounded-lg 
                     hover:bg-olive/80 disabled:opacity-50 
                     disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
          </form>
        </div>
      </div>
    </div>
  );

  // Embedded mode: no backdrop, no animations, fills container
  if (embedded) {
    if (!isOpen) return null;
    return chatContent;
  }

  return (
    <>
      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[60]"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      {/* Chat Window - Dark Theme */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 20 }}
            className={`fixed z-[61] bg-charcoal shadow-2xl flex flex-col ${
              isMobile
                ? 'inset-0' // Fullscreen on mobile
                : 'inset-0 m-auto w-full max-w-4xl h-[800px] rounded-2xl border border-ash/20'
            }`}
            style={{
              height: isMobile ? '100vh' : '800px',
              maxHeight: isMobile ? '100vh' : '800px',
              // Use CSS transform for better mobile performance
              transform: isMobile ? 'translate3d(0, 0, 0)' : undefined,
              position: 'fixed',
              // Handle safe areas on iOS
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : undefined,
              // Prevent pull-to-refresh and viewport manipulation
              touchAction: 'pan-y',
              // Ensure proper stacking context
              isolation: 'isolate'
            }}
          >
            {/* Header - Minimal */}
            <div className="flex items-center justify-between p-4 border-b border-ash/20">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-olive to-rust flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-bone" />
                </div>
                <h3 className="font-semibold text-bone">Remy</h3>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-ash/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-ash hover:text-bone" />
              </button>
            </div>

            {/* Context bar: Trip Settings & (optional) Voice toggle */}
            <div className="px-4 pt-2 pb-2 border-b border-ash/20 flex items-center gap-2 overflow-x-auto">
              <button
                onClick={() => setShowTripSettings(v => !v)}
                className="text-xs bg-parchment/10 text-bone px-2 py-1 rounded-lg hover:bg-ash/20 border border-ash/20 flex items-center gap-1"
              >
                <Users className="w-3.5 h-3.5 text-olive" />
                <span>{partySize} {partySize === 1 ? 'person' : 'people'}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              <button
                onClick={() => setShowTripSettings(v => !v)}
                className="text-xs bg-parchment/10 text-bone px-2 py-1 rounded-lg hover:bg-ash/20 border border-ash/20 flex items-center gap-1"
              >
                <Calendar className="w-3.5 h-3.5 text-olive" />
                <span>{selectedDates.length > 0 ? `${selectedDates.length} day${selectedDates.length>1?'s':''}` : 'Add dates'}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              {isRealtimeVoiceEnabled && (
                <button
                  onClick={connectRealtime}
                  className={clsx(
                    'ml-auto text-xs px-2 py-1 rounded-lg border transition-colors',
                    isRealtimeConnected ? 'bg-olive/20 text-olive border-olive/40' : 'bg-ash/20 text-bone border-ash/30 hover:bg-ash/30'
                  )}
                >
                  Voice {isRealtimeConnected ? 'On' : 'Beta'}
                </button>
              )}
            </div>

            {showTripSettings && (
              <div className="px-4 py-3 border-b border-ash/20 bg-charcoal-lighter/60">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs text-ash">Party size</span>
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5,6,7,8].map(n => (
                      <button
                        key={n}
                        onClick={() => setPartySize(n)}
                        className={clsx('px-2 py-1 rounded text-xs border', partySize===n ? 'bg-olive text-bone border-olive' : 'bg-charcoal text-bone border-ash/30 hover:bg-ash/20')}
                      >
                        {n}
                      </button>
                    ))}
                    <button
                      onClick={() => setPartySize(10)}
                      className="px-2 py-1 rounded text-xs border bg-charcoal text-bone border-ash/30 hover:bg-ash/20"
                    >
                      10+
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-ash">When</span>
                  <div className="flex items-center gap-1 flex-wrap">
                    {['Today','Tomorrow','This weekend','Next week'].map(option => {
                      const isSelected = selectedDates.includes(option);
                      return (
                        <button
                          key={option}
                          onClick={() => {
                            setSelectedDates(prev => isSelected ? prev.filter(d=>d!==option) : [...prev, option]);
                          }}
                          className={clsx('px-2 py-1 rounded text-xs border', isSelected ? 'bg-olive text-bone border-olive' : 'bg-charcoal text-bone border-ash/30 hover:bg-ash/20')}
                        >
                          {option}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Saved Panel Toggle + Smart Pills */}
            {/* Saved panel toggle removed to reduce duplication; keep header indicator only */}

            {/* Smart Pills - persistent adaptive actions */}
            <div className="flex gap-2 p-3 border-b border-ash/20 overflow-x-auto">
              {smartPills.map((pill, idx) => (
                <button
                  key={idx}
                  onClick={() => handlePillClick(pill)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-charcoal-lighter 
                           hover:bg-ash/20 rounded-full text-sm whitespace-nowrap 
                           transition-colors text-bone border border-ash/20"
                >
                  <pill.icon className="w-3.5 h-3.5 text-olive" />
                  {pill.text}
                </button>
              ))}
            </div>

            {/* Saved Panel */}
            {showSavedPanel && (savedPlaces?.length || 0) > 0 && (
              <div className="border-b border-ash/20 bg-charcoal-lighter/60">
                <div className="max-h-44 overflow-y-auto p-3 space-y-2">
                  {savedPlaces.map((sp: any) => (
                    <div key={sp.id} className="flex items-center justify-between bg-charcoal border border-ash/20 rounded-lg px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-sm text-bone truncate">{sp.place_name || sp.name}</p>
                        {sp.category && (
                          <p className="text-xs text-ash truncate">{sp.category}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        {sp.google_maps_link && (
                          <a
                            href={sp.google_maps_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-olive hover:text-olive/80"
                          >
                            Map
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Chat Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {chatContent}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export const RemyChat = withProfiler(RemyChatComponent);
